import { sortBy } from "lodash";

import { PATIENT_PRODUCT_STATUSES } from "constants/patient-products";
import {
  BATTERY_SUBTYPE,
  CHARGER_SUBTYPE,
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  EAR_TYPES,
  EXAM,
  OTHER_SUBTYPE,
  PRODUCT_SUB_TYPES,
  RECEIVER_SUBTYPE,
  REMOTE_SUBTYPE,
  REPAIR_SUBTYPE,
  TUBE_SUBTYPE,
  WARRANTY_SUBTYPE
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SERVICE_PRODUCT_TYPE,
  SUPPLY_PRODUCT_TYPE
} from "constants/sales";
import { PatientProduct } from "services/types/patient-products.type";
import { formatDateOnlyValue } from "utils/common";

export const subTypeOrder = new Map<number, number>();

subTypeOrder.set(EAR_MOLD_SUBTYPE, 0);
subTypeOrder.set(TUBE_SUBTYPE, 1);
subTypeOrder.set(DOME_SUBTYPE, 2);
subTypeOrder.set(RECEIVER_SUBTYPE, 3);
subTypeOrder.set(BATTERY_SUBTYPE, 4);
subTypeOrder.set(CHARGER_SUBTYPE, 5);
subTypeOrder.set(OTHER_SUBTYPE, 6);
subTypeOrder.set(REMOTE_SUBTYPE, 7);
subTypeOrder.set(WARRANTY_SUBTYPE, 8);
subTypeOrder.set(EXAM, 9);
subTypeOrder.set(REPAIR_SUBTYPE, 10);

export const typeOrder = new Map<number, number>();
typeOrder.set(HEARING_AID_PRODUCT_TYPE, 0);
typeOrder.set(ACCESSORY_PRODUCT_TYPE, 1);
typeOrder.set(SERVICE_PRODUCT_TYPE, 2);
typeOrder.set(FEE_PRODUCT_TYPE, 3);
typeOrder.set(SUPPLY_PRODUCT_TYPE, 4);

const ID_MARKER_REGEX = /\s*\(#\d+\)/g;

// "Short Description" tag: "Manufacturer Model Style, S/N: <serial>".
const getProductDescriptionShort = (product: PatientProduct) => {
  const masterProduct = product?.inventory_product?.product?.master_product;

  // Style repeats the "Product" column for accessories ("Accessory (Dome)").
  const type = masterProduct?.type || product.type;
  const style =
    type === ACCESSORY_PRODUCT_TYPE ? undefined : masterProduct?.subtype_display;

  const name =
    [masterProduct?.manufacturer?.name, masterProduct?.model, style]
      .filter(Boolean)
      .join(" ") ||
    [
      product?.inventory_product?.product?.display_name,
      masterProduct?.display_name,
      product.description,
      [product?.order_item?.description, product?.order_item?.model]
        .filter(Boolean)
        .join(" ")
    ].find(Boolean) ||
    "";

  const serial =
    product.serial_number || product?.inventory_product?.serial_number;

  return [name.replace(ID_MARKER_REGEX, "").trim(), serial ? `S/N: ${serial}` : ""]
    .filter(Boolean)
    .join(", ");
};

const getProductTypeText = (product: PatientProduct) => {
  const type =
    product.inventory_product?.product?.master_product?.type || product.type;

  const subtype =
    product.inventory_product?.product?.master_product?.subtype ||
    product.subtype;

  if (!type) return "";

  if (type && subtype) {
    return `${PRODUCT_TYPES.getFromCode(
      type
    )} (${PRODUCT_SUB_TYPES.getFromCode(subtype)})`;
  } else if (
    !product.sale &&
    !product.inventory_product &&
    !product.order_item &&
    (product.type === HEARING_AID_PRODUCT_TYPE ||
      product.type === ACCESSORY_PRODUCT_TYPE)
  ) {
    return `${PRODUCT_TYPES.getFromCode(type)} (Existing)`;
  }

  return PRODUCT_TYPES.getFromCode(type);
};

const getSortedProductsByTypeAndSubtype = (products: PatientProduct[]) => {
  return sortBy(products, [
    (product) =>
      typeOrder.get(
        Number(
          product.inventory_product?.product?.master_product?.type ||
            product.type
        )
      ) ?? Number.MAX_SAFE_INTEGER,
    (product) =>
      product.subtype
        ? (subTypeOrder.get(
            product.inventory_product?.product?.master_product?.subtype ||
              product.subtype
          ) ?? Number.MAX_SAFE_INTEGER)
        : Number.MAX_SAFE_INTEGER
  ]);
};

export const getSortedProducts = (products: PatientProduct[]) => {
  const leftEar = products.filter((product) => product.ear === "L") || [];
  const rightEar = products.filter((product) => product.ear === "R") || [];
  const noEar =
    products.filter((product) => product.ear !== "L" && product.ear !== "R") ||
    [];

  return [
    ...getSortedProductsByTypeAndSubtype(leftEar),
    ...getSortedProductsByTypeAndSubtype(rightEar),
    ...getSortedProductsByTypeAndSubtype(noEar)
  ];
};

export const getHearingAidsTableHtml = (params: {
  products: PatientProduct[];
  addStatus?: boolean;
  addWarranties?: boolean;
}) => {
  const { addStatus, addWarranties } = params;
  const products = getSortedProducts(params.products);

  let descriptionWidth = 75;
  if (addStatus) {
    descriptionWidth -= 15;
  }
  if (addWarranties) {
    descriptionWidth -= 15;
  }

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  <colgroup>
    <col style="width: 10%;">
    <col style="width: 15%;">
    <col style="width: ${descriptionWidth}%;">
    ${addStatus ? '<col style="width: 15%;">' : ""}
    ${addWarranties ? '<col style="width: 7.5%;"><col style="width: 7.5%;">' : ""}

  </colgroup>
<tbody>
    <tr>
      <td><strong>Ear</strong></td>
      <td><strong>Product</strong></td>
      <td><strong>Description</strong></td>
      ${addStatus ? "<td><strong>Status</strong></td>" : ""}
      ${addWarranties ? "<td><strong>MFR&nbsp;Warranty</strong></td><td><strong>L&D&nbsp;Warranty</strong></td>" : ""}
    </tr>`;

  products.forEach((product) => {
    let row = `<td>${EAR_TYPES.getFromCode(product.ear) ?? ""}</td>`;

    row = row + `<td>${getProductTypeText(product)}</td>`;
    row = row + `<td>${getProductDescriptionShort(product)}</td>`;
    if (addStatus) {
      row =
        row +
        `<td>${PATIENT_PRODUCT_STATUSES.getFromCode(product.status)}</td>`;
    }
    if (addWarranties) {
      const mfrDate =
        product.manufacturer_warranty_expiration_date ||
        product.inventory_product?.manufacturer_warranty_expiration_date;

      const mfr = mfrDate
        ? `${formatDateOnlyValue(mfrDate, "MMM dd, yyyy")}`
        : "";

      const lndDate =
        product.loss_damage_warranty_expiration_date ||
        product.inventory_product?.loss_damage_warranty_expiration_date;

      const lnd = lndDate
        ? `${formatDateOnlyValue(lndDate, "MMM dd, yyyy")}`
        : "";
      row = row + `<td>${mfr}</td><td>${lnd}</td>`;
    }

    result = result + "<tr>" + row + "</tr>";
  });

  result = result + `</tbody></table>`;
  return result;
};

// "Short Description" tag: one product per line, "[Ear] [Product]: [description]".
export const getActiveProductsShortDescriptionHtml = (
  products: PatientProduct[]
) =>
  getSortedProducts(products)
    .map((product) => {
      const label = [EAR_TYPES.getFromCode(product.ear), getProductTypeText(product)]
        .filter(Boolean)
        .join(" ");
      return `<p><strong>${label}:</strong> ${getProductDescriptionShort(product)}</p>`;
    })
    .join("");
